// Organization Types
export interface Organization {
  id: string;
  name: string;
  description?: string;
  currency: string;
  language: string;
  monthly_dues_amount: number;
  join_code?: string;
  created_by: string;
  created_at?: string;
  updated_at?: string;
  subscription_status?: 'active' | 'inactive' | 'trial' | 'cancelled';
  subscription_plan?: 'free' | 'basic' | 'intermediate' | 'advanced';
  subscription_expires_at?: string;
  member_portal_enabled?: boolean;
  trial_ends_at?: string;
  // Additional fields for settings
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo_url?: string | null;
}

export interface UserOrganization {
  id?: string;
  user_id: string;
  organization_id: string;
  role: 'admin' | 'treasurer' | 'secretary' | 'member';
  is_active: boolean;
  joined_at?: string;
  organization?: Organization;
}

// User Types
export interface User {
  id: string;
  email: string;
  full_name?: string;
  phone?: string;
  address?: string;
  avatar_url?: string;
  created_at?: string;
  updated_at?: string;
}

// User Role Type
export type UserRole = 'admin' | 'treasurer' | 'secretary' | 'member';

// Member Types
export interface Member {
  id: string;
  organization_id: string;
  user_id?: string;
  full_name: string;
  email?: string;
  phone?: string;
  address?: string;
  join_date: string;
  status: 'active' | 'inactive' | 'suspended';
  notes?: string;
  created_at?: string;
  updated_at?: string;
  // Additional fields
  title?: string;
  date_of_birth?: string;
  emergency_contact?: string;
  occupation?: string;
  // Financial fields
  dues_balance?: number;
  credit_balance?: number;
}

// Member Titles
export const memberTitles = [
  'Mr',
  'Mrs',
  'Ms',
  'Dr',
  'Prof',
  'Rev',
  'Hon',
  'Chief',
  'Alhaji',
  'Alhaja',
  'Other',
];

// Role Display Names
export const roleDisplayNames: Record<UserRole, string> = {
  admin: 'Administrator',
  treasurer: 'Treasurer',
  secretary: 'Secretary',
  member: 'Member',
};

// Attendance Types
export interface Meeting {
  id: string;
  organization_id: string;
  meeting_date: string;
  meeting_type: 'Regular' | 'Special' | 'Event';
  opening_cash?: number;
  closing_cash?: number;
  notes?: string;
  collectors?: string[];
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  // Additional fields used in UI
  attendance_count?: number;
  total_members?: number;
}

export interface AttendanceRecord {
  id?: string;
  meeting_id: string;
  member_id: string;
  is_present: boolean;
  check_in_time?: string;
  notes?: string;
  created_at?: string;
  member?: Member;
}

// Dues Types
export interface DuePayment {
  id?: string;
  organization_id: string;
  member_id: string;
  amount: number;
  payment_month: string;
  payment_year: number;
  payment_date?: string;
  payment_method?: 'cash' | 'bank_transfer' | 'mobile_money' | 'card' | 'other';
  status: 'pending' | 'paid' | 'overdue' | 'waived';
  notes?: string;
  recorded_by?: string;
  created_at?: string;
  member?: Member;
}

// Donation Types
export interface DonationEvent {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  target_amount?: number;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  created_by?: string;
  created_at?: string;
  // Additional fields used in UI
  total_raised?: number;
  donor_count?: number;
}

export interface Donation {
  id?: string;
  event_id: string;
  donor_name: string;
  donor_email?: string;
  donor_phone?: string;
  amount: number;
  payment_method?: 'cash' | 'bank_transfer' | 'mobile_money' | 'card' | 'other';
  payment_status: 'pending' | 'completed' | 'failed';
  notes?: string;
  is_anonymous: boolean;
  recorded_by?: string;
  created_at?: string;
}

// Finance Types
export interface FinancialTransaction {
  id?: string;
  organization_id: string;
  transaction_type: 'income' | 'expense';
  category: string;
  amount: number;
  description: string;
  transaction_date: string;
  payment_method?: 'cash' | 'bank_transfer' | 'mobile_money' | 'card' | 'other';
  reference_number?: string;
  recorded_by: string;
  created_at?: string;
}

// Subscription Plan Type (string literal)
export type SubscriptionPlan = 'free' | 'basic' | 'intermediate' | 'advanced';

// Subscription Plan Details Interface
export interface SubscriptionPlanDetails {
  id: SubscriptionPlan;
  name: string;
  price: number;
  interval: 'monthly' | 'yearly';
  features: string[];
  max_members: number;
  stripe_price_id?: string;
  flutterwave_plan_id?: string;
  paystack_plan_id?: string;
}

export interface Subscription {
  id?: string;
  organization_id: string;
  plan_id: string;
  status: 'active' | 'cancelled' | 'past_due' | 'unpaid';
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  payment_provider?: 'stripe' | 'flutterwave' | 'paystack';
  payment_provider_subscription_id?: string;
  created_at?: string;
}

// Currency and Language Types
export interface Currency {
  code: string;
  name: string;
  symbol: string;
}

export interface Language {
  code: string;
  name: string;
}

// Currencies List
export const currencies: Currency[] = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
  { code: 'XOF', name: 'West African CFA', symbol: 'CFA' },
  { code: 'XAF', name: 'Central African CFA', symbol: 'FCFA' },
];

// Currency Symbols Map
export const currencySymbols: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  NGN: '₦',
  GHS: '₵',
  KES: 'KSh',
  ZAR: 'R',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
  BRL: 'R$',
  MXN: '$',
  XOF: 'CFA',
  XAF: 'FCFA',
};

// Languages List
export const languages: Language[] = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français (French)' },
  { code: 'es', name: 'Español (Spanish)' },
  { code: 'it', name: 'Italiano (Italian)' },
  { code: 'de', name: 'Deutsch (German)' },
  { code: 'zh', name: '中文 (Chinese)' },
];

// Subscription Plans
export const subscriptionPlans: SubscriptionPlanDetails[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    interval: 'monthly',
    features: [
      'Up to 20 members',
      'Basic attendance tracking',
      'Simple dues collection',
      'Email notifications',
      'Basic reporting',
    ],
    max_members: 20,
  },
  {
    id: 'basic',
    name: 'Basic',
    price: 5,
    interval: 'monthly',
    features: [
      'Up to 50 members',
      'Advanced attendance tracking',
      'Dues collection with reminders',
      'Donation management',
      'Basic finance tracking',
      'Priority email support',
    ],
    max_members: 50,
  },
  {
    id: 'intermediate',
    name: 'Intermediate',
    price: 10,
    interval: 'monthly',
    features: [
      'Up to 80 members',
      'All Basic features',
      'Advanced finance management',
      'Member portal access',
      'Custom reports',
      'SMS notifications',
      'API access',
    ],
    max_members: 80,
  },
  {
    id: 'advanced',
    name: 'Advanced',
    price: 15,
    interval: 'monthly',
    features: [
      'Up to 200 members',
      'All Intermediate features',
      'White-label options',
      'Dedicated support',
      'Custom integrations',
      'Advanced analytics',
      'SSO authentication',
    ],
    max_members: 200,
  },
];

export const PLAN_MEMBER_LIMITS: Record<SubscriptionPlan, number> = {
  free: 20,
  basic: 50,
  intermediate: 80,
  advanced: 200,
};
